/**
 * SearchResultsTable - Sortable/Filterable table for search results
 *
 * Features:
 * - Column sorting (all columns)
 * - Client-side filtering for therapeutic areas, region, dates
 * - URL state persistence for filters
 * - Infinite scroll (via intersection observer)
 * - Row click navigates to drug detail
 */
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  FilterFn,
} from '@tanstack/react-table';
import {
  DrugResultV2,
  SearchRegion,
} from '../types';
import { regionDisplayLabel, isUsFdaVisible, REGION_OPTIONS, isRegionDisabled, getDisabledRegionTooltip, ADMIN_ONLY_REGIONS } from '../constants/filterConstants';
import Tooltip from './Tooltip';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { ChevronUp, ChevronDown, ChevronRight, ChevronsUpDown, X, Filter, Check, Calendar, Search, Copy, CheckCheck, Layers } from 'lucide-react';
import { sectionEvidenceLabel } from './viewer/tocHelpers';
import { approvalYear, formatApprovalDate } from '../utils/approvalDate';
import { cogniRefineFields, matchesRefineChips, parseRefineParam, serializeRefineChips, type RefineChip } from '../utils/refineQuery';
import RefineChipBar from './RefineChipBar';
import HaDocumentSubList from './HaDocumentSubList';
import {
  COGNI_RESULTS_REGION_PARAM,
  isUndatedEffectiveTime,
  matchesApprovalYear,
  matchesLastUpdated,
  matchesRegion,
  type ApprovalYearRange,
  type LastUpdatedRange,
} from '../utils/cogniTableFilters';

// Column filters delegate to utils/cogniTableFilters — the same predicates
// the Compare page's Cogni rail applies, so both agree on which rows pass.
const regionFilter: FilterFn<DrugResultV2> = (row, columnId, filterValue: SearchRegion[]) =>
  matchesRegion(row.getValue<SearchRegion | undefined>(columnId), filterValue);

const approvalYearFilter: FilterFn<DrugResultV2> = (row, _columnId, filterValue: ApprovalYearRange) =>
  !filterValue || matchesApprovalYear(row.original.approval_date, filterValue);

const lastUpdatedFilter: FilterFn<DrugResultV2> = (row, _columnId, filterValue: LastUpdatedRange) =>
  !filterValue || matchesLastUpdated(row.original.effective_time, filterValue);

// Custom filter for the Refine chip bar: chips of the same kind are ORed
// (region/year/brand/generic/manufacturer), text chips are ANDed with each
// other, and different kinds are ANDed together — see refineQuery.ts for
// the full matching contract.
// Built per-render from `showApprovalDate`: with the Approval Date column
// hidden, a typed text chip like "2019" must not match a date the user cannot
// see (same rule as the suggestion population below).
const makeRefineFilter = (includeApprovalDate: boolean): FilterFn<DrugResultV2> =>
  (row, _columnId, filterValue: RefineChip[]) =>
    matchesRefineChips(cogniRefineFields(row.original, includeApprovalDate), filterValue);

// Format date for display (YYYY-MM-DD format)
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '-';
  }
};

// Non-compact "Label updated" display (MMM D, YYYY). UTC so a date-only
// string such as 2026-06-09 never renders as Jun 8 west of UTC.
const formatLabelUpdated = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

// Last Updated for all regions: some regions' drugs.effective_time is only
// known to month precision at the source (e.g. JP). Rendering those at day
// precision would fabricate a day that was never derived.
const LAST_UPDATED_PRECISION: Partial<Record<string, 'month' | 'day'>> = { JP: 'month' };

// Non-compact "Label updated" cell text, shared by the flat tanstack-rendered
// column and the grouped hand-built `renderDrugRow` path below — both must
// apply the same precision decision. Compact mode (year-only) is handled
// separately by each caller, unaffected by precision.
const formatLastUpdatedCell = (dateStr: string | null | undefined, region: string | null | undefined): string => {
  if (LAST_UPDATED_PRECISION[region ?? ''] === 'month') {
    return formatApprovalDate(dateStr, 'month') ?? '-';
  }
  return formatLabelUpdated(dateStr);
};

const columnHelper = createColumnHelper<DrugResultV2>();
const RESULTS_REGION_PARAM = COGNI_RESULTS_REGION_PARAM;

interface SearchResultsTableProps {
  results: DrugResultV2[];
  onDrugClick: (drugId: number, result?: DrugResultV2) => void;
  selectedDrugIds?: number[];
  onDrugSelect?: (drugId: number) => void;
  maxCompare?: number;
  /** Compact mode for sidebar usage - smaller text, no checkbox column, vertical filter layout */
  compact?: boolean;
  /** Currently viewed drug ID - highlighted in compact mode */
  currentDrugId?: number;
  /** Show relevance score instead of rank position */
  showRelevanceScore?: boolean;
  /** Whether the Approval Year column + filter exist at all — header, every
   *  row's cell, and the Approval Year filter panel/state contribution,
   *  never a per-row check. Approval dates are reliable for every region
   *  (2026-10-15 backfill), so every caller now passes true except a surface
   *  that intentionally omits the column entirely. */
  showApprovalDate: boolean;
}

const SearchResultsTable: React.FC<SearchResultsTableProps> = ({
  results,
  onDrugClick,
  selectedDrugIds = [],
  onDrugSelect,
  maxCompare = 10,
  compact = false,
  currentDrugId,
  showRelevanceScore = false,
  showApprovalDate,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsStringRef = useRef(searchParams.toString());
  const setSearchParamsRef = useRef(setSearchParams);
  searchParamsStringRef.current = searchParams.toString();
  setSearchParamsRef.current = setSearchParams;
  // Admins bypass UK/CA/AU region greyout on the in-table region filter.
  const isAdmin = useIsAdmin();
  const usFdaVisible = isUsFdaVisible(isAdmin);

  const initialRegionsParam = searchParams.get(RESULTS_REGION_PARAM);
  const initialYearMinParam = searchParams.get('yearMin');
  const initialYearMaxParam = searchParams.get('yearMax');
  const initialIncludeUnknownParam = searchParams.get('includeUnknown');
  const initialUpdatedStartParam = searchParams.get('updatedStart');
  const initialUpdatedEndParam = searchParams.get('updatedEnd');
  const initialIncludeUndatedParam = searchParams.get('includeUndated');
  const initialRefineParam = searchParams.get('refine');
  const initialGroupByParam = searchParams.get('groupBy');

  // Table state - no default sort (results come pre-sorted by relevance from API)
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Evidence chunk expansion state
  const [expandedChunkId, setExpandedChunkId] = useState<number | null>(null);
  const [copiedChunkId, setCopiedChunkId] = useState<number | null>(null);

  // Filter UI state
  const [showFilters, setShowFilters] = useState(() =>
    Boolean(initialRegionsParam || initialYearMinParam || initialYearMaxParam || initialUpdatedStartParam || initialUpdatedEndParam)
  );
  const [selectedRegions, setSelectedRegions] = useState<SearchRegion[]>(() =>
    initialRegionsParam ? (initialRegionsParam.split(',').filter(Boolean) as SearchRegion[]) : []
  );

  // Approval Year filter state
  const [approvalYearMin, setApprovalYearMin] = useState<number | null>(() =>
    initialYearMinParam ? parseInt(initialYearMinParam, 10) : null
  );
  const [approvalYearMax, setApprovalYearMax] = useState<number | null>(() =>
    initialYearMaxParam ? parseInt(initialYearMaxParam, 10) : null
  );
  const [includeUnknownApproval, setIncludeUnknownApproval] = useState(() =>
    initialIncludeUnknownParam !== 'false'
  );

  // Last Updated filter state
  const [lastUpdatedStart, setLastUpdatedStart] = useState<string | null>(() => initialUpdatedStartParam);
  const [lastUpdatedEnd, setLastUpdatedEnd] = useState<string | null>(() => initialUpdatedEndParam);
  const [includeUndatedUpdated, setIncludeUndatedUpdated] = useState(() =>
    initialIncludeUndatedParam !== 'false'
  );

  // Refine results chip filter state
  const [refineChips, setRefineChips] = useState<RefineChip[]>(() => parseRefineParam(initialRefineParam));

  // Group by Generic Name state
  const [groupByGeneric, setGroupByGeneric] = useState(() => initialGroupByParam === 'generic');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Calculate dynamic year range from data
  const { minYear, maxYear, unknownCount } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    let unknown = 0;

    results.forEach((result) => {
      const year = approvalYear(result.approval_date);
      if (year === null) {
        unknown++;
        return;
      }
      if (year < min) min = year;
      if (year > max) max = year;
    });

    return {
      minYear: min === Infinity ? null : min,
      maxYear: max === -Infinity ? null : max,
      unknownCount: unknown,
    };
  }, [results]);

  // Count of undated (null or unparseable effective_time) rows, for the
  // "Include undated" toggle label.
  const undatedUpdatedCount = useMemo(
    () => results.reduce((count, result) => count + (isUndatedEffectiveTime(result.effective_time) ? 1 : 0), 0),
    [results],
  );

  // Generate year options for dropdowns
  const yearOptions = useMemo(() => {
    if (minYear === null || maxYear === null) return [];
    const years: number[] = [];
    for (let y = maxYear; y >= minYear; y--) {
      years.push(y);
    }
    return years;
  }, [minYear, maxYear]);

  // Update URL when filters change.
  //
  // Table-only region filtering uses ``resultRegions`` instead of the
  // search-owned ``regions`` param. The top-level Cogni search reads
  // ``regions`` as part of the backend request identity, so results-table
  // toggles must never rewrite it or they will trigger a new search.
  // The refs keep the callback independent from React Router's URL object
  // identity, and the serialized equality check avoids same-URL replace
  // navigations that can still cascade through SearchPage's URL sync.
  const updateUrlFilters = useCallback(() => {
    const params = new URLSearchParams(searchParamsStringRef.current);

    // Region filter
    if (selectedRegions.length > 0) {
      params.set(RESULTS_REGION_PARAM, selectedRegions.join(','));
    } else {
      params.delete(RESULTS_REGION_PARAM);
    }

    // Approval year filter
    if (approvalYearMin !== null) {
      params.set('yearMin', approvalYearMin.toString());
    } else {
      params.delete('yearMin');
    }
    if (approvalYearMax !== null) {
      params.set('yearMax', approvalYearMax.toString());
    } else {
      params.delete('yearMax');
    }
    if (!includeUnknownApproval) {
      params.set('includeUnknown', 'false');
    } else {
      params.delete('includeUnknown');
    }

    // Last updated filter
    if (lastUpdatedStart) {
      params.set('updatedStart', lastUpdatedStart);
    } else {
      params.delete('updatedStart');
    }
    if (lastUpdatedEnd) {
      params.set('updatedEnd', lastUpdatedEnd);
    } else {
      params.delete('updatedEnd');
    }
    if (!includeUndatedUpdated) {
      params.set('includeUndated', 'false');
    } else {
      params.delete('includeUndated');
    }

    // Refine chip filter
    const serializedRefine = serializeRefineChips(refineChips);
    if (serializedRefine) {
      params.set('refine', serializedRefine);
    } else {
      params.delete('refine');
    }

    // Group by generic
    if (groupByGeneric) {
      params.set('groupBy', 'generic');
    } else {
      params.delete('groupBy');
    }

    const next = params.toString();
    if (next === searchParamsStringRef.current) return;
    setSearchParamsRef.current(params, { replace: true });
  }, [selectedRegions, approvalYearMin, approvalYearMax, includeUnknownApproval, lastUpdatedStart, lastUpdatedEnd, includeUndatedUpdated, refineChips, groupByGeneric]);

  // Apply filters to table
  useEffect(() => {
    const newFilters: ColumnFiltersState = [];

    if (selectedRegions.length > 0) {
      newFilters.push({
        id: 'region',
        value: selectedRegions,
      });
    }

    // Approval year filter — only applies when the column is shown; a
    // hidden filter must not silently drop rows.
    if (showApprovalDate && (approvalYearMin !== null || approvalYearMax !== null || !includeUnknownApproval)) {
      newFilters.push({
        id: 'approval_date',
        value: { min: approvalYearMin, max: approvalYearMax, includeUnknown: includeUnknownApproval },
      });
    }

    // Last updated filter
    if (lastUpdatedStart || lastUpdatedEnd || !includeUndatedUpdated) {
      newFilters.push({
        id: 'effective_time',
        value: { start: lastUpdatedStart, end: lastUpdatedEnd, includeUndated: includeUndatedUpdated },
      });
    }

    // Refine chip filter
    if (refineChips.length > 0) {
      newFilters.push({
        id: 'drug_name',
        value: refineChips,
      });
    }

    setColumnFilters(newFilters);
    updateUrlFilters();
  }, [selectedRegions, approvalYearMin, approvalYearMax, includeUnknownApproval, lastUpdatedStart, lastUpdatedEnd, includeUndatedUpdated, refineChips, groupByGeneric, showApprovalDate, updateUrlFilters]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSelectedRegions([]);
    setApprovalYearMin(null);
    setApprovalYearMax(null);
    setIncludeUnknownApproval(true);
    setLastUpdatedStart(null);
    setLastUpdatedEnd(null);
    setIncludeUndatedUpdated(true);
    setRefineChips([]);
    setGroupByGeneric(false);
    setExpandedGroups(new Set());
  }, []);

  // Toggle region selection
  const toggleRegion = useCallback((region: SearchRegion) => {
    setSelectedRegions(prev =>
      prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
    );
  }, []);

  // Define columns
  const columns = useMemo(
    () => [
      // Selection checkbox column (hidden in compact mode, and when no
      // ``onDrugSelect`` handler is wired — compare now lives on the
      // dedicated /compare-keyword page via the ★ shortlist, not checkboxes).
      ...(compact || !onDrugSelect ? [] : [columnHelper.display({
        id: 'select',
        header: () => null,
        cell: ({ row }) => {
          const isSelected = selectedDrugIds.includes(row.original.drug_id);
          const isMaxReached = selectedDrugIds.length >= maxCompare;
          const canSelect = isSelected || !isMaxReached;

          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (canSelect && onDrugSelect) onDrugSelect(row.original.drug_id);
              }}
              disabled={!canSelect}
              className={`
                w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                ${isSelected
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : canSelect
                    ? 'border-slate-300 hover:border-indigo-400'
                    : 'border-slate-200 bg-slate-50 cursor-not-allowed'
                }
              `}
              title={isSelected ? 'Remove from comparison' : isMaxReached ? `Max ${maxCompare} drugs` : 'Add to comparison'}
            >
              {isSelected && <Check className="w-3 h-3" />}
            </button>
          );
        },
        size: 40,
      })]),

      // Rank / relevance score column - sortable
      columnHelper.accessor('score', {
        id: 'rank',
        header: showRelevanceScore ? 'Score' : '#',
        cell: ({ row }) => {
          if (!showRelevanceScore) {
            return (
              <span className={`inline-flex items-center justify-center ${compact ? 'px-1 py-0.5 text-[10px] min-w-[20px]' : 'px-1.5 py-0.5 text-xs min-w-[24px]'} font-medium text-slate-600`}>
                {row.index + 1}
              </span>
            );
          }
          const score = Math.round(row.original.score);
          const colorClass = score >= 90
            ? 'bg-emerald-200 text-emerald-800'
            : score >= 75
              ? 'bg-emerald-100 text-emerald-700'
              : score >= 50
                ? 'bg-amber-100 text-amber-700'
                : score >= 20
                  ? 'bg-red-100 text-red-600'
                  : 'bg-red-200 text-red-800';
          return (
            <span className={`inline-flex items-center justify-center rounded-full ${compact ? 'px-1 py-0.5 text-[10px] min-w-[20px]' : 'px-1.5 py-0.5 text-xs min-w-[28px]'} font-semibold ${colorClass}`}>
              {score}
            </span>
          );
        },
        sortingFn: 'basic',
        size: compact ? 30 : 45,
      }),

      // Internal numeric drug id (drugs.id) — admin/debugging aid. Hidden in
      // compact mode (2026-07-08 Cogni ID/Manufacturer columns).
      ...(compact ? [] : [columnHelper.accessor('drug_id', {
        id: 'drug_id_display',
        header: 'ID',
        cell: ({ getValue }) => (
          <span className="font-mono text-[13px] text-ink-3 tabular-nums">{getValue()}</span>
        ),
        sortingFn: 'basic',
        size: 60,
      })]),

      // Drug name column - V2 uses drug_name instead of brand_name
      columnHelper.accessor(
        (row) => row.drug_name || 'Unknown Drug',
        {
          id: 'drug_name',
          header: 'Drug',
          cell: ({ row }) => (
            <div className="flex flex-col">
              <span className={compact ? 'font-medium text-indigo-600 hover:text-indigo-800 text-xs truncate max-w-[120px]' : 'text-[15px] font-semibold text-ink'}>
                {row.original.drug_name || 'Unknown Drug'}
              </span>
              {!compact && row.original.generic_name && (
                <span className="text-[13px] text-ink-3">{row.original.generic_name}</span>
              )}
            </div>
          ),
          filterFn: makeRefineFilter(showApprovalDate),
          size: compact ? 130 : 120,
        }
      ),

      // Manufacturer (drugs.manufacturer) — optional field; "-" for rows from
      // pre-change cached payloads or drugs without one. Hidden in compact mode.
      ...(compact ? [] : [columnHelper.accessor(
        (row) => row.manufacturer || '',
        {
          id: 'manufacturer',
          header: 'Manufacturer',
          cell: ({ row }) => {
            const m = row.original.manufacturer;
            if (!m) return <span className="text-sm text-slate-400">-</span>;
            return (
              <span className="text-[13px] text-ink-2 truncate block max-w-[140px]" title={m}>
                {m}
              </span>
            );
          },
          size: 140,
        }
      )]),

      // Evidence text column (hidden in compact mode)
      ...(compact ? [] : [columnHelper.accessor(
        (row) => row.evidence_text || '',
        {
          id: 'evidence_text',
          header: 'Evidence',
          cell: ({ row }) => {
            const evidence = row.original.evidence;
            const text = row.original.evidence_text || evidence?.snippet;
            const sectionLabel = evidence
              ? sectionEvidenceLabel(evidence.section_number, evidence.section_title)
              : null;
            const chunkText = evidence?.chunk_text;

            if (!text && !sectionLabel) return <span className="text-sm text-slate-400">-</span>;
            return (
              <div className="flex flex-col gap-0.5">
                {sectionLabel && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDrugClick(row.original.drug_id, row.original);
                    }}
                    className="inline-flex items-center self-start px-1.5 py-0.5 font-mono text-[11px] bg-accent-tint text-accent-deep rounded truncate max-w-[280px]"
                  >
                    {sectionLabel}
                  </button>
                )}
                {(text || chunkText) && (
                  <div className="flex items-start gap-1">
                    {text ? (
                      <p className="text-[13px] text-ink-2 line-clamp-2 flex-1">
                        {text}
                      </p>
                    ) : (
                      <p className="text-[13px] text-slate-400 flex-1">
                        View evidence chunk
                      </p>
                    )}
                    {chunkText && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedChunkId(
                            expandedChunkId === row.original.drug_id ? null : row.original.drug_id
                          );
                        }}
                        className="flex-shrink-0 mt-0.5 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                        title="View full evidence chunk"
                      >
                        <Search className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
                {expandedChunkId === row.original.drug_id && chunkText && (
                  <div
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(chunkText);
                      } catch {
                        const textarea = document.createElement('textarea');
                        textarea.value = chunkText;
                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand('copy');
                        document.body.removeChild(textarea);
                      }
                      setCopiedChunkId(row.original.drug_id);
                      setTimeout(() => setCopiedChunkId(null), 2000);
                    }}
                    className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 max-h-40 overflow-y-auto whitespace-pre-wrap cursor-pointer hover:bg-slate-100 transition-colors group relative"
                    title="Click to copy"
                  >
                    <span className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {copiedChunkId === row.original.drug_id
                        ? <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />
                        : <Copy className="w-3.5 h-3.5 text-slate-400" />
                      }
                    </span>
                    {chunkText}
                  </div>
                )}
              </div>
            );
          },
          size: 400,
        }
      )]),

      // Approval year column — exists while showApprovalDate is true (every
      // real caller, as of the 2026-10-15 backfill). See DrugResultsTable's
      // showApprovalDate for the Keyword/Instant equivalent.
      ...(showApprovalDate ? [columnHelper.accessor('approval_date', {
        header: compact ? 'Year' : 'Approved',
        // Year-only by design in BOTH modes (spec 2026-09-21 §6.4, line 75:
        // "`SearchResultsTable.tsx` already shows the year only —
        // unchanged. Year filters unchanged." — unlike DrugDetailPage,
        // this table does NOT render at
        // approval_date_precision). Passing
        // precision through here regressed rows with no
        // approval_date_precision (cached responses from before PR #284):
        // formatApprovalDate falls back to 'day' and a Jan-1 year-precision
        // value renders as a fabricated full date ("1/1/2004") instead of
        // "2004". `formatApprovalDate(dateStr, 'year')` is used only for its
        // correct year derivation, never `new Date(...).getFullYear()`,
        // which parses a date-only string as UTC midnight and rolls that
        // same Jan-1 value back a year west of GMT. It already returns null
        // for a missing/unparseable date, preserving the '-' empty state
        // below without a try/catch.
        cell: ({ getValue }) => {
          const dateStr = getValue();
          const text = formatApprovalDate(dateStr, 'year');
          if (!text) return <span className={`${compact ? 'text-xs' : 'text-sm'} text-slate-400`}>-</span>;
          return <span className={compact ? 'text-xs text-slate-600' : 'font-mono text-[13px] text-ink-2'}>{text}</span>;
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.approval_date;
          const b = rowB.original.approval_date;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        filterFn: approvalYearFilter,
        size: compact ? 50 : 120,
      })] : []),

      // Most recent update column
      columnHelper.accessor('effective_time', {
        header: compact ? 'Updated' : 'Label updated',
        cell: ({ getValue, row }) => {
          if (!compact) {
            return <span className="font-mono text-[13px] text-ink-2">{formatLastUpdatedCell(getValue(), row.original.region)}</span>;
          }
          // In compact mode, show just year for space
          const formatted = formatDate(getValue());
          const display = formatted !== '-' ? formatted.substring(0, 4) : formatted;
          return <span className="text-xs text-slate-600">{display}</span>;
        },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.effective_time;
          const b = rowB.original.effective_time;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return new Date(a).getTime() - new Date(b).getTime();
        },
        filterFn: lastUpdatedFilter,
        size: compact ? 55 : 130,
      }),

      // Region column
      columnHelper.accessor('region', {
        header: 'Geo',
        cell: ({ getValue }) => {
          const region = regionDisplayLabel(getValue() || 'US', usFdaVisible);
          // In compact mode, show just the region code (US, EU, UK)
          if (!compact) {
            return <span className="font-mono text-[13px] text-ink-2">{region}</span>;
          }
          return (
            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-700">
              {region}
            </span>
          );
        },
        filterFn: regionFilter,
        size: compact ? 40 : 100,
      }),

    ],
    [selectedDrugIds, maxCompare, onDrugSelect, compact, showRelevanceScore, showApprovalDate, expandedChunkId, copiedChunkId, usFdaVisible]
  );

  // Toggle a group's expanded state
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  // Compute grouped data structure
  const genericGroups = useMemo(() => {
    if (!groupByGeneric) return null;

    const groups = new Map<string, DrugResultV2[]>();
    for (const result of results) {
      const key = (result.generic_name || 'Unknown Generic').toLowerCase().trim();
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(result);
    }

    // Sort groups by best score (highest first)
    const sorted = Array.from(groups.entries()).sort((a, b) => {
      const bestA = Math.max(...a[1].map(r => r.score));
      const bestB = Math.max(...b[1].map(r => r.score));
      return bestB - bestA;
    });

    // Sort drugs within each group by score descending
    for (const [, drugs] of sorted) {
      drugs.sort((a, b) => b.score - a.score);
    }

    return sorted;
  }, [results, groupByGeneric]);

  // A stale sort on approval_date (e.g. carried over from a prior US-only
  // search) must not be handed to the table once its column is omitted from
  // `columns` above — TanStack expects every sorting entry's id to resolve
  // to a live column. `sorting` state itself is left alone, so the sort
  // comes back if the column returns.
  const effectiveSorting = useMemo(
    () => (showApprovalDate ? sorting : sorting.filter((s) => s.id !== 'approval_date')),
    [sorting, showApprovalDate],
  );

  // Create table instance
  const table = useReactTable({
    data: results,
    columns,
    state: {
      sorting: effectiveSorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
  });

  // Rows passing every OTHER column filter (region/approval-year/last-updated)
  // but NOT the drug_name column's own filter, i.e. not the refine chips —
  // the base population the Refine chip bar's suggestion counts are computed
  // against. TanStack's faceted row model for a column excludes that
  // column's own filter by construction.
  const baseFacetedRows = table.getColumn('drug_name')!.getFacetedRowModel().rows;
  const baseRows = useMemo(
    () => baseFacetedRows.map((r) => cogniRefineFields(r.original, showApprovalDate)),
    [baseFacetedRows, showApprovalDate],
  );

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const hasActiveFilters = selectedRegions.length > 0 ||
    (showApprovalDate && (approvalYearMin !== null || approvalYearMax !== null || !includeUnknownApproval)) ||
    lastUpdatedStart !== null ||
    lastUpdatedEnd !== null ||
    !includeUndatedUpdated ||
    refineChips.length > 0 ||
    groupByGeneric;

  // Count active filter groups for badge
  const activeFilterCount = [
    selectedRegions.length > 0,
    showApprovalDate && (approvalYearMin !== null || approvalYearMax !== null || !includeUnknownApproval),
    lastUpdatedStart !== null || lastUpdatedEnd !== null || !includeUndatedUpdated,
    refineChips.length > 0,
    groupByGeneric,
  ].filter(Boolean).length;

  // Nested HA review documents (corpus="ha_reviews"); label rows carry none
  // and render exactly as before.
  const renderHaDocsRow = (drug: DrugResultV2) => (
    <tr key={`ha-docs-${drug.drug_id}`}>
      <td colSpan={table.getVisibleFlatColumns().length} className={compact ? 'px-2 pb-2' : 'px-3 pb-3'}>
        <HaDocumentSubList drugId={drug.drug_id} documents={drug.ha_documents ?? []} />
      </td>
    </tr>
  );

  return (
    <div className="w-full">
      {/* Filter Controls + Refine Search (single row) */}
      <div className={compact ? 'mb-2' : 'mb-3'}>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              inline-flex items-center gap-1.5 ${compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1.5 text-xs'} font-medium rounded-lg transition-colors shrink-0
              ${showFilters || hasActiveFilters
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
              }
            `}
          >
            <Filter className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            Filters
            {hasActiveFilters && (
              <span className={`inline-flex items-center justify-center ${compact ? 'w-4 h-4 text-[10px]' : 'w-4 h-4 text-[10px]'} bg-indigo-600 text-white rounded-full`}>
                {activeFilterCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className={`inline-flex items-center gap-1 px-2 py-1 ${compact ? 'text-[10px]' : 'text-xs'} text-slate-500 hover:text-slate-700 shrink-0`}
            >
              <X className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
              Clear
            </button>
          )}

          <button
            onClick={() => {
              setGroupByGeneric(prev => !prev);
              setExpandedGroups(new Set());
            }}
            className={compact
              ? `
              inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-lg transition-colors shrink-0
              ${groupByGeneric
                ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
              }
            `
              : `btn-secondary inline-flex items-center gap-1.5 text-xs shrink-0 ${groupByGeneric ? 'ring-1 ring-accent-ring' : ''}`}
          >
            <Layers className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            {compact ? 'Group' : 'Group: generic'}
          </button>

          <span className={`${compact ? 'text-[10px]' : 'text-xs'} text-slate-500 shrink-0`}>
            {filteredRowCount} of {results.length}
          </span>

          {/* Refine chip bar inline */}
          {compact ? (
            <RefineChipBar chips={refineChips} onChipsChange={setRefineChips} baseRows={baseRows} compact={compact} usFdaVisible={usFdaVisible} />
          ) : (
            <div className="flex-1 min-w-[240px] border border-line rounded-lg bg-white px-2 py-1">
              <RefineChipBar
                chips={refineChips}
                onChipsChange={setRefineChips}
                baseRows={baseRows}
                compact={compact}
                usFdaVisible={usFdaVisible}
                placeholder="Filter by brand, generic, manufacturer, year or text"
              />
            </div>
          )}
        </div>

        {/* Expandable Filter Panel */}
        {showFilters && (
          <div className={`${compact ? 'p-2' : 'p-4'} bg-slate-50 rounded-lg border border-slate-200`}>
            <div className={compact ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-3 gap-6'}>
              {/* Region Filter */}
              <div>
                <label className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-slate-700 mb-1.5 block`}>Region</label>
                <div className="flex flex-wrap gap-1.5">
                  {/* CN and other admin-only regions are never offered on the
                       results-filter surface (Investigator-only) — see
                       ADMIN_ONLY_REGIONS / isRegionVisible in filterConstants. */}
                  {REGION_OPTIONS.filter(({ value }) => !ADMIN_ONLY_REGIONS.has(value)).map(({ value, label }) => {
                    const regionDisabled = isRegionDisabled(value, isAdmin);
                    const btn = (
                      <button
                        key={value}
                        onClick={() => { if (!regionDisabled) toggleRegion(value); }}
                        disabled={regionDisabled}
                        className={`
                          ${compact ? 'px-2 py-1 text-xs' : 'px-2.5 py-1 text-xs'} font-medium rounded-lg border transition-all duration-150
                          ${selectedRegions.includes(value)
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                          }
                          ${regionDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        {regionDisplayLabel(value, usFdaVisible, label)}
                      </button>
                    );
                    // Browsers suppress pointer events on disabled buttons,
                    // so we put the tooltip on a non-disabled wrapper.
                    return regionDisabled ? (
                      <Tooltip key={value} text={getDisabledRegionTooltip(value)}>{btn}</Tooltip>
                    ) : btn;
                  })}
                </div>
              </div>

              {/* Approval Year Filter — exists while showApprovalDate is true. */}
              {showApprovalDate && (
              <div>
                <label className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-slate-700 mb-1.5 block`}>
                  {compact ? 'Year' : 'Approval Year'}
                  {minYear && maxYear && (
                    <span className="font-normal text-slate-400 ml-1">
                      ({minYear}–{maxYear})
                    </span>
                  )}
                </label>
                <div className="space-y-1.5">
                  {/* Year range dropdowns */}
                  <div className="flex items-center gap-1.5">
                    <select
                      value={approvalYearMin ?? ''}
                      onChange={(e) => setApprovalYearMin(e.target.value ? parseInt(e.target.value, 10) : null)}
                      className={`flex-1 ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg border border-slate-300 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                    >
                      <option value="">From</option>
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <span className="text-slate-400 text-xs">–</span>
                    <select
                      value={approvalYearMax ?? ''}
                      onChange={(e) => setApprovalYearMax(e.target.value ? parseInt(e.target.value, 10) : null)}
                      className={`flex-1 ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg border border-slate-300 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
                    >
                      <option value="">To</option>
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  {/* Include unknown toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeUnknownApproval}
                      onChange={(e) => setIncludeUnknownApproval(e.target.checked)}
                      className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded border-slate-300 text-indigo-600 focus:ring-indigo-500`}
                    />
                    <span className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-600`}>
                      Include unknown <span className="text-slate-400">({unknownCount})</span>
                    </span>
                  </label>
                </div>
              </div>
              )}

              {/* Last Updated Filter */}
              <div>
                <label className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-slate-700 mb-1.5 block flex items-center gap-1`}>
                  <Calendar className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
                  {compact ? 'Updated' : 'Last Updated'}
                </label>
                <div className="space-y-1.5">
                  {/* Quick presets */}
                  <div className="flex flex-wrap gap-1">
                    {[
                      { label: compact ? '30d' : 'Last 30 days', days: 30 },
                      { label: compact ? '6mo' : 'Last 6 months', days: 180 },
                      { label: compact ? '1yr' : 'Last year', days: 365 },
                    ].map(({ label, days }) => {
                      const presetStart = new Date();
                      presetStart.setDate(presetStart.getDate() - days);
                      const isoString = presetStart.toISOString();
                      const presetStartStr = isoString.substring(0, 10); // YYYY-MM-DD
                      const isActive = lastUpdatedStart === presetStartStr && !lastUpdatedEnd;

                      return (
                        <button
                          key={label}
                          onClick={() => {
                            if (isActive) {
                              setLastUpdatedStart(null);
                            } else {
                              setLastUpdatedStart(presetStartStr);
                              setLastUpdatedEnd(null);
                            }
                          }}
                          className={`
                            ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs'} rounded-md transition-colors
                            ${isActive
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white border border-slate-300 text-slate-600 hover:border-indigo-400'
                            }
                          `}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {/* Custom date range - hide in compact mode to save space */}
                  {!compact && (
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={lastUpdatedStart ?? ''}
                        onChange={(e) => setLastUpdatedStart(e.target.value || null)}
                        className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Start date"
                      />
                      <span className="text-slate-400">–</span>
                      <input
                        type="date"
                        value={lastUpdatedEnd ?? ''}
                        onChange={(e) => setLastUpdatedEnd(e.target.value || null)}
                        className="flex-1 px-2 py-1.5 text-sm rounded-lg border border-slate-300 bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="End date"
                      />
                    </div>
                  )}
                  {/* Include undated toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeUndatedUpdated}
                      onChange={(e) => setIncludeUndatedUpdated(e.target.checked)}
                      className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} rounded border-slate-300 text-indigo-600 focus:ring-indigo-500`}
                    />
                    <span className={`${compact ? 'text-[10px]' : 'text-sm'} text-slate-600`}>
                      Include undated <span className="text-slate-400">({undatedUpdatedCount})</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* Table */}
      <div className="border border-line rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-ground border-b border-line">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`${compact ? 'px-2 py-1.5' : 'px-3 py-2'} text-left ${compact ? 'text-[10px]' : 'text-xs'} font-semibold text-ink-2 uppercase tracking-wide`}
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder ? null : header.column.getCanSort() ? (
                        <button
                          onClick={header.column.getToggleSortingHandler()}
                          className="flex items-center gap-0.5 hover:text-slate-900"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <ChevronUp className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-indigo-600`} />,
                            desc: <ChevronDown className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-indigo-600`} />,
                          }[header.column.getIsSorted() as string] ?? (
                            <ChevronsUpDown className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-slate-400`} />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-line">
              {groupByGeneric && genericGroups ? (
                // Grouped rendering with running rank counter
                (() => {
                  // Build a set of drug_ids that pass the table filters
                  const filteredIds = new Set(
                    table.getFilteredRowModel().rows.map(r => r.original.drug_id)
                  );
                  const colCount = table.getVisibleFlatColumns().length;
                  let globalRank = 0;

                  // Helper to render a single drug row
                  const renderDrugRow = (drug: DrugResultV2, rank: number, indent: boolean) => {
                    const isCurrentDrug = compact && currentDrugId === drug.drug_id;
                    return (
                      <React.Fragment key={`${indent ? 'child' : 'single'}-${drug.drug_id}`}>
                      <tr
                        onClick={() => onDrugClick(drug.drug_id, drug)}
                        className={`cursor-pointer transition-colors ${isCurrentDrug ? 'bg-indigo-100 hover:bg-indigo-150' : 'hover:bg-indigo-50/50'}`}
                      >
                        {/* Select cell must render under exactly the same condition
                            as its header column: ``...(compact || !onDrugSelect ? [] : [...])``.
                            Without the onDrugSelect gate this row gains an extra empty
                            cell whenever the caller (e.g. SearchPage) omits the prop. */}
                        {!compact && onDrugSelect && (
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedDrugIds.includes(drug.drug_id)}
                              onChange={() => onDrugSelect(drug.drug_id)}
                              disabled={!selectedDrugIds.includes(drug.drug_id) && selectedDrugIds.length >= maxCompare}
                              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                            />
                          </td>
                        )}
                        <td className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}>
                          <span className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500`}>{rank}</span>
                        </td>
                        {!compact && (
                          <td className="px-3 py-2">
                            <span className="font-mono text-[13px] text-ink-3 tabular-nums">{drug.drug_id}</span>
                          </td>
                        )}
                        <td className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}>
                          <div className={`flex flex-col ${indent && !compact ? 'pl-6' : ''}`}>
                            <span className={compact ? 'font-medium text-indigo-600 hover:text-indigo-800 text-xs truncate max-w-[120px]' : 'text-[15px] font-semibold text-ink'}>
                              {drug.drug_name || 'Unknown Drug'}
                            </span>
                            {!compact && !indent && drug.generic_name && (
                              <span className="text-[13px] text-ink-3">{drug.generic_name}</span>
                            )}
                          </div>
                        </td>
                        {!compact && (
                          <td className="px-3 py-2">
                            {drug.manufacturer ? (
                              <span className="text-[13px] text-ink-2 truncate block max-w-[140px]" title={drug.manufacturer}>
                                {drug.manufacturer}
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400">-</span>
                            )}
                          </td>
                        )}
                        {!compact && (
                          <td className="px-3 py-2">
                            {(() => {
                              const evidence = drug.evidence;
                              const sectionLabel = evidence
                                ? sectionEvidenceLabel(
                                  evidence.section_number,
                                  evidence.section_title,
                                )
                                : null;
                              const evText = drug.evidence_text || evidence?.snippet;
                              if (!evText && !sectionLabel) {
                                return <span className="text-sm text-slate-400">-</span>;
                              }
                              return (
                                <div className="flex flex-col gap-0.5">
                                  {sectionLabel && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onDrugClick(drug.drug_id, drug);
                                      }}
                                      className="inline-flex items-center self-start px-1.5 py-0.5 font-mono text-[11px] bg-accent-tint text-accent-deep rounded truncate max-w-[280px]"
                                    >
                                      {sectionLabel}
                                    </button>
                                  )}
                                  {evText && (
                                    <p className="text-[13px] text-ink-2 line-clamp-2">{evText}</p>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                        {showApprovalDate && (
                        <td className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}>
                          <span className={compact ? 'text-xs text-slate-600' : 'font-mono text-[13px] text-ink-2'}>
                            {/* Year-only by design, both modes — see the flat column's cell above. */}
                            {formatApprovalDate(drug.approval_date, 'year') ?? '-'}
                          </span>
                        </td>
                        )}
                        <td className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}>
                          <span className={compact ? 'text-xs text-slate-600' : 'font-mono text-[13px] text-ink-2'}>
                            {compact ? (drug.effective_time ? formatDate(drug.effective_time).substring(0, 4) : formatDate(drug.effective_time)) : formatLastUpdatedCell(drug.effective_time, drug.region)}
                          </span>
                        </td>
                        <td className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}>
                          <span className={compact ? 'inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-slate-100 text-slate-700' : 'font-mono text-[13px] text-ink-2'}>
                            {drug.region || 'US'}
                          </span>
                        </td>
                      </tr>
                      {drug.ha_documents?.length ? renderHaDocsRow(drug) : null}
                      </React.Fragment>
                    );
                  };

                  return genericGroups.map(([groupKey, drugs]) => {
                    const filteredDrugs = drugs.filter(d => filteredIds.has(d.drug_id));
                    if (filteredDrugs.length === 0) return null;

                    const isExpanded = expandedGroups.has(groupKey);
                    const firstDrug = filteredDrugs[0]!;
                    const displayName = firstDrug.generic_name || 'Unknown Generic';
                    const isSingleDrug = filteredDrugs.length === 1;

                    // For single-drug groups, render as a normal row
                    if (isSingleDrug) {
                      globalRank++;
                      return renderDrugRow(firstDrug, globalRank, false);
                    }

                    // Multi-drug group: render header + optionally expanded children
                    const groupStartRank = globalRank + 1;
                    globalRank += filteredDrugs.length;

                    return (
                      <React.Fragment key={`group-${groupKey}`}>
                        {/* Group header row */}
                        <tr
                          onClick={() => toggleGroup(groupKey)}
                          className="cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <td colSpan={colCount} className={compact ? 'px-2 py-1.5' : 'px-4 py-2.5'}>
                            <div className="flex items-center gap-2">
                              <ChevronRight
                                className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-slate-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              />
                              <span className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-slate-700`}>
                                {displayName}
                              </span>
                              <span className={`inline-flex items-center justify-center ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'} font-medium rounded-full bg-indigo-100 text-indigo-700`}>
                                {compact ? `(${filteredDrugs.length})` : `${filteredDrugs.length} drugs`}
                              </span>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded child rows */}
                        {isExpanded && filteredDrugs.map((drug, idx) =>
                          renderDrugRow(drug, groupStartRank + idx, true)
                        )}
                      </React.Fragment>
                    );
                  });
                })()
              ) : (
                // Default flat rendering
                table.getRowModel().rows.map((row) => {
                  const isCurrentDrug = compact && currentDrugId === row.original.drug_id;
                  return (
                    <React.Fragment key={row.id}>
                    <tr
                      onClick={() => onDrugClick(row.original.drug_id, row.original)}
                      className={`
                        cursor-pointer transition-colors
                        ${isCurrentDrug
                          ? 'bg-indigo-100 hover:bg-indigo-150'
                          : 'hover:bg-indigo-50/50'
                        }
                      `}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={compact ? 'px-2 py-1.5' : 'px-3 py-2'}
                          onClick={(e) => {
                            // Prevent row click when clicking checkbox
                            if (cell.column.id === 'select') {
                              e.stopPropagation();
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {row.original.ha_documents?.length ? renderHaDocsRow(row.original) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {table.getRowModel().rows.length === 0 && (
          <div className={compact ? 'py-6 text-center' : 'py-12 text-center'}>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500`}>No results match your filters</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className={`mt-2 ${compact ? 'text-xs' : 'text-sm'} text-indigo-600 hover:text-indigo-800`}
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResultsTable;
